// mislinks/src/Pages/TallyImportdata.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getTallyContra,
  getTallyPayment,
  getTallyPurchase,
  getTallyReceipt,
  getTallySales,
  uploadTallyBankExcel,
  getTallyData
} from '../api/misApis';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import '../Assets/Css/alert.css';

const SOURCES = [
  { key: 'contra',   label: 'TallyContra',   loader: getTallyContra },
  { key: 'payment',  label: 'TallyPayment',  loader: getTallyPayment },
  { key: 'purchase', label: 'TallyPurchase', loader: getTallyPurchase },
  { key: 'receipt',  label: 'TallyReceipt',  loader: getTallyReceipt },
  { key: 'sales',    label: 'TallySales',    loader: getTallySales },
];

// Purchase table and Excel columns, in the required Tally import order.
const PURCHASE_COLUMNS = [
  'Voucher Type',
  'Voucher No.',
  'Date',
  'Party A/c - Vendor',
  'Batch No',
  'Item Name',
  'Item Description',
  'Item Qty',
  'Item Rate',
  'Amount',
  'Sales A/c',
  'Party amount',
  'Input CGST 2.5%',
  'Input SGST 2.5%',
  'Input CGST 6%',
  'Input SGST 6%',
  'Input CGST 9%',
  'Input SGST 9%',
  'Input IGST 5% TAX',
  'Input IGST 12%',
  'Input IGST 18% TAX',
  'Carriage Inward',
  'Counting Expenses',
  'Transportation Charges (Purchase)',
  'Purchase Loading Unloading Exp',
  'Tds On Goods (194 Q)',
  'Round off',
  'Narration',
];

const PURCHASE_SOURCE_KEYS = {
  'Transportation Charges (Purchase)': 'Transport',
  'Purchase Loading Unloading Exp': 'Loading UnLoading',
};

const formatPurchaseLedger = (value) => typeof value === 'string'
  ? value.replace(/^Purchase (I?GST) (5|12|18)%$/, 'Purchase $1 @ $2%')
  : value;

const formatPurchaseRow = (row) => Object.fromEntries(
  PURCHASE_COLUMNS.map((column) => {
    const value = row[PURCHASE_SOURCE_KEYS[column] || column] ?? null;
    return [column, column === 'Sales A/c' ? formatPurchaseLedger(value) : value];
  })
);

function TallyImportdata() {
  const navigate = useNavigate();

  // which table is selected
  const [activeKey, setActiveKey] = useState('contra');

  // cached rows per table: { contra: [...], payment: [...], ... }
  const [rowsByKey, setRowsByKey] = useState({});

  // UI state
  const [loading, setLoading] = useState(false);

  // filter state (applies to active table only when user clicks Filter)
  const [filterText, setFilterText] = useState('');
  const [filteredRows, setFilteredRows] = useState([]);

 // 🔹 Date range (applies ONLY to the column literally named "Date")
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState(''); 

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  //const rowsPerPage = 10;
  const [rowsPerPage, setRowsPerPage] = useState(10); // 🔹 User selectable
  // ---- Import controls state (NEW) ----
  const [bank, setBank] = useState('');           // 'HDFC' or 'AXIS'
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
const fileInputRef = useRef(null);
  // ✅ Memoize activeRows to satisfy eslint react-hooks/exhaustive-deps
  const activeRows = useMemo(
    () => rowsByKey[activeKey] ?? [],
    [rowsByKey, activeKey]
  );

  // Load the selected table if not cached
  useEffect(() => {
    if (rowsByKey[activeKey]) return;
    const src = SOURCES.find((s) => s.key === activeKey);
    if (!src) return;
    setLoading(true);
    src
      .loader()
      .then((res) => {
        const data = Array.isArray(res?.data) ? res.data : [];
        setRowsByKey((prev) => ({
          ...prev,
          [activeKey]: activeKey === 'purchase' ? data.map(formatPurchaseRow) : data,
        }));
      })
      .catch((err) => console.error('Fetch error:', err))
      .finally(() => setLoading(false));
  }, [activeKey, rowsByKey]);

  // When switching table or when activeRows refresh, reset filter/view
  useEffect(() => {
    setFilteredRows(activeRows);
    setFilterText('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  }, [activeRows]);

  // ✅ Memoize columns; use memoized activeRows to avoid eslint warning
  const columns = useMemo(() => {
    if (activeKey === 'purchase') return PURCHASE_COLUMNS;
    const sample = (filteredRows && filteredRows[0]) || (activeRows && activeRows[0]) || null;
    return sample ? Object.keys(sample) : [];
  }, [activeKey, filteredRows, activeRows]);



   // ✅ Only use a column literally named "Date"
  const hasDateCol = useMemo(() => columns.includes('Date'), [columns]);

  // helpers
  const parseDate = (val) => {
    if (val === null || val === undefined || val === '') return null;
    
    // Handle DD-MM-YYYY or DD/MM/YYYY format
    if (typeof val === 'string') {
      const parts = val.split(/[-/]/);
      if (parts.length === 3 && parts[2].length === 4) {
        // parts[0] = DD, parts[1] = MM, parts[2] = YYYY
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JS
        const year = parseInt(parts[2], 10);
        
        const d = new Date(year, month, day);
        if (!isNaN(d.getTime())) return d;
      }
    }

    // Default fallback
    const d1 = new Date(val);
    if (!isNaN(d1.getTime())) return d1;
    const d2 = new Date(String(val).replace(/\//g, '-'));
    if (!isNaN(d2.getTime())) return d2;
    return null;
  };

  const withinDateRange = (row) => {
    if (!hasDateCol || (!dateFrom && !dateTo)) return true; // date filter only if "Date" exists and user set a range
    const d = parseDate(row['Date']);
    if (!d) return false;
    if (dateFrom) {
      const from = new Date(dateFrom + 'T00:00:00');
      if (d < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      if (d > to) return false;
    }
    return true;
  };


    // apply combined filters (text + date-on-"Date")
  const applyFilter = () => {
    const q = filterText.trim().toLowerCase();
    const out = activeRows.filter(row => {
      const passText = q
        ? Object.values(row).some(v => String(v ?? '').toLowerCase().includes(q))
        : true;
      const passDate = withinDateRange(row);
      return passText && passDate;
    });
    setFilteredRows(out);
    setCurrentPage(1);
  };

  // Clear filter and show all rows of the selected table
  const clearFilter = () => {
    setFilterText('');
    setDateFrom('');
    setDateTo('');
    setFilteredRows(activeRows);
    setCurrentPage(1);
  };

  // Export the filtered rows of the selected table
  const exportFiltered = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(
      filteredRows,
      activeKey === 'purchase' ? { header: PURCHASE_COLUMNS } : undefined
    );
    const label = SOURCES.find((s) => s.key === activeKey)?.label ?? 'Export';
    XLSX.utils.book_append_sheet(wb, ws, label);
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${label}_filtered.xlsx`);
  };

  // pagination slice (applies after filter)
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const pageRows = filteredRows.slice(startIndex, startIndex + rowsPerPage);

  const showToast = (message, type = 'success') => {
  const alertPlaceholder = document.getElementById("alertPlaceholder");

  const isSuccess = type === 'success';
  const bgClass = isSuccess ? 'bg-green-100' : 'bg-red-100';
  const borderClass = isSuccess ? 'border-green-400' : 'border-red-400';
  const textClass = isSuccess ? 'text-green-700' : 'text-red-700';
  const icon = isSuccess ? '✅ Success:' : '❌ Error:';

  alertPlaceholder.innerHTML = `
    <div class="alert-box ${bgClass} ${borderClass} ${textClass} px-4 py-3 rounded shadow-lg text-left animate-fadeIn w-fit mx-auto">
      <strong class="font-bold">${icon}</strong>
      <span class="block sm:inline ml-1">${message}</span>
    </div>
  `;

  setTimeout(() => {
    alertPlaceholder.innerHTML = "";
  }, 2500);
};
// const fmtMonths = (arr) => (Array.isArray(arr) && arr.length ? arr.join(', ') : '-');

const handleImport = async () => {
  if (!bank) {
    showToast('Please Select the Bank', 'error');   // requested message
    return;
  }
  if (!file) {
    showToast('Please choose an .xlsx file.', 'error');
    return;
  }

  try {
    setImporting(true);

    const res = await uploadTallyBankExcel(file, bank);
    const { inserted, table, error } = res?.data || {};

    if (error) {
      showToast(error, 'error');
      return;
    }

    showToast(`Imported ${inserted ?? 0} rows into ${table}.`, 'success');

    // ✅ Only reset UI controls (no table reload)
    setBank('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  } catch (e) {
    console.error('Import failed:', e);
    const msg = e?.response?.data?.error || e?.message || 'Import failed.';
    showToast(msg, 'error');
  } finally {
    setImporting(false);
  }
};


  const [selectedBank, setSelectedBank] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [month, setMonth] = useState("");
  const [bankRows, setBankRows] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [filterType, setFilterType] = useState("range"); // 'range' or 'month'
  const [bankPage, setBankPage] = useState(1);
  const bankRowsPerPage = 10;

  const bankTotalPages = Math.ceil(bankRows.length / bankRowsPerPage);
  const bankStart = (bankPage - 1) * bankRowsPerPage;
  const bankPageRows = bankRows.slice(bankStart, bankStart + bankRowsPerPage);

  const fetchTallyData = async () => {
    if (!selectedBank) {
      showToast("Please select bank", "error");
      return;
    }

    try {
      setBankLoading(true);

      const res = await getTallyData({
        bank: selectedBank,
        startDate,
        endDate,
        month,
      });

      const data = res?.data?.data || [];
      setBankRows(data);

    } catch (err) {
      console.error(err);
      showToast("Failed to fetch data", "error");
    } finally {
      setBankLoading(false);
    }
  };

  return (
    <div className="p-4">
      <div id="alertPlaceholder"></div>

      {/* top bar */}
      <div className="mb-4 flex justify-between items-center flex-wrap gap-4">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-800 text-sm"
        >
          ⬅️ Back
        </button>

        {/* table selector buttons */}
        <div className="flex flex-wrap gap-2">
          {SOURCES.map((s) => (
            <button
              key={s.key}
              onClick={() => {
                setActiveKey(s.key);
                // load if not cached
                if (!rowsByKey[s.key]) {
                  setLoading(true);
                  s
                    .loader()
                    .then((res) => {
                      const data = Array.isArray(res?.data) ? res.data : [];
                      setRowsByKey((prev) => ({ ...prev, [s.key]: data }));
                    })
                    .catch(console.error)
                    .finally(() => setLoading(false));
                }
              }}
              className={`px-3 py-1 rounded text-sm border ${
                activeKey === s.key
                  ? 'bg-blue-600 text-white border-blue-700'
                  : 'bg-white hover:bg-gray-50'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* filter + export controls for the active table */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="text"
          placeholder={`Filter in ${SOURCES.find((s) => s.key === activeKey)?.label}`}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="p-2 border rounded text-sm"
          style={{ minWidth: 280 }}
        />

        {/* 🔹 Date range controls only if "Date" column exists */}
        {hasDateCol && (
          <>
            <label className="text-sm text-gray-600 ml-2">Date:</label>
            <input
              type="date"
              className="p-2 border rounded text-sm"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <span className="text-sm text-gray-600">to</span>
            <input
              type="date"
              className="p-2 border rounded text-sm"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </>
        )}
        <button onClick={applyFilter} className="px-3 py-1 bg-gray-800 text-white rounded text-sm">
          Filter
        </button>
        <button onClick={clearFilter} className="px-3 py-1 bg-gray-200 rounded text-sm">
          Clear
        </button>
        <button onClick={exportFiltered} className="px-3 py-1 bg-green-600 text-white rounded text-sm">
          Export (filtered)
        </button>
        {/* ---- Import controls (NEW) ---- */}
        <div className="flex items-center gap-2">
          <select
            value={bank}
            onChange={(e) => setBank(e.target.value)}
            className="p-1 border rounded text-sm"
            title="Select Bank"
          >
            <option value="">Select Bank</option>
            <option value="HDFC">HDFC</option>
            <option value="AXIS">AXIS</option>
          </select>

          <input
            ref={fileInputRef}  // <-- add this
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm border rounded p-1"
            title="Choose Tally Excel"
          />

          <button
            onClick={handleImport}
            //disabled={importing || !bank || !file}
            className={`px-3 py-1 rounded text-sm flex items-center gap-2
              ${
                importing
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-indigo-600 text-white hover:bg-indigo-800'
              }`}
          >
            {importing && (
              <span className="loader border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
            )}
            {importing ? 'Importing...' : 'Import'}
          </button>
        </div>

          {/* 🔹 Rows per page selector */}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-sm text-gray-600">Rows per page:</label>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1); // reset to first page
            }}
            className="p-1 border rounded text-sm"
          >
            <option value={10}>10</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      

      {/* data grid */}
      <div className="overflow-auto border rounded shadow">
        {loading ? (
          <div className="p-6 text-sm text-gray-600">Loading…</div>
        ) : (
          <table className="min-w-[900px] w-full text-sm border-collapse">
            <thead className="bg-gray-100">
              <tr>
                {columns.map((col) => (
                  <th key={col} className="border px-2 py-2 text-left whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={Math.max(columns.length, 1)}
                    className="border px-2 py-3 text-center text-gray-500"
                  >
                    No rows
                  </td>
                </tr>
              ) : (
                pageRows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    {columns.map((col) => (
                      <td key={col} className="border px-2 py-1 whitespace-nowrap">
                        {String(row[col] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* pagination */}
      <div className="mt-3 flex justify-between items-center">
        <span className="text-sm">
          Page {currentPage} of {totalPages}
        </span>
        <div className="space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* NEW FILTER DIV (HDFC / AXIS DATA FETCH) */}
      <div className="mt-5 p-4 border rounded shadow bg-gray-50">

        <h3 className="text-sm font-semibold mb-3">
          Filter Tally Bank Data (HDFC / AXIS)
        </h3>

        {/* FILTER CONTROLS */}
        <div className="flex flex-wrap items-center gap-3">

          {/* Bank */}
          <select
            value={selectedBank}
            onChange={(e) => setSelectedBank(e.target.value)}
            className="p-2 border rounded text-sm"
          >
            <option value="">Select Bank</option>
            <option value="HDFC">HDFC</option>
            <option value="AXIS">AXIS</option>
          </select>

          {/* Toggle */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="p-2 border rounded text-sm"
          >
            <option value="range">Date Range</option>
            <option value="month">Month</option>
          </select>

          {/* Date Range */}
          {filterType === "range" && (
            <>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="p-2 border rounded text-sm"
              />
              <span className="text-sm">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="p-2 border rounded text-sm"
              />
            </>
          )}

          {/* 🔥 Month */}
          {filterType === "month" && (
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="p-2 border rounded text-sm"
            />
          )}

          {/* Fetch */}
          <button
            onClick={fetchTallyData}
            disabled={bankLoading}
            className={`px-3 py-1 rounded text-sm flex items-center gap-2
              ${
                bankLoading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-800'
              }`}
          >
            {bankLoading && (
              <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
            )}
            {bankLoading ? 'Loading...' : 'Fetch Data'}
          </button>

        </div>

        {/* SEPARATE LOADER */}
        {bankLoading && (
          <div className="flex justify-center items-center py-4">
            <div className="border-4 border-blue-500 border-t-transparent rounded-full w-6 h-6 animate-spin"></div>
            <span className="ml-2 text-sm">Fetching data...</span>
          </div>
        )}

        {/* TABLE */}
        {bankRows.length > 0 && !bankLoading && (
          <>
            <div className="mt-4 overflow-auto border rounded">
              <table className="min-w-[900px] w-full text-sm border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    {Object.keys(bankRows[0]).map((col) => (
                      <th key={col} className="border px-2 py-2 text-left">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bankPageRows.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      {Object.keys(row).map((col) => (
                        <td key={col} className="border px-2 py-1">
                          {col === "Date"
                            ? new Date(row[col]).toLocaleDateString("en-GB")
                            : String(row[col] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            <div className="mt-3 flex justify-between items-center">
              <span className="text-sm">
                Page {bankPage} of {bankTotalPages || 1}
              </span>

              <div className="space-x-2">
                <button
                  onClick={() => setBankPage(p => Math.max(p - 1, 1))}
                  disabled={bankPage === 1}
                  className="px-2 py-1 bg-gray-200 rounded"
                >
                  Prev
                </button>

                <button
                  onClick={() => setBankPage(p => Math.min(p + 1, bankTotalPages))}
                  disabled={bankPage === bankTotalPages}
                  className="px-2 py-1 bg-gray-200 rounded"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {/*  EMPTY STATE */}
        {!bankLoading && bankRows.length === 0 && (
          <p className="text-sm text-gray-500 mt-3 text-center">
            No data found
          </p>
        )}

      </div>

    </div>
  );
}

export default TallyImportdata;
